import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { Header } from "./components/Header";
import { HeroSection } from "./components/HeroSection";
import { ModuleCards } from "./components/ModuleCards";
import { Footer } from "./components/Footer";
import { getSession } from "./lib/api";
import EvaluatePage from "./pages/EvaluatePage";
import EvaluateRfpClientInfoPage from "./pages/EvaluateRfpClientInfoPage";
import EvaluateRfpIntakePage from "./pages/EvaluateRfpIntakePage";
import EvaluateRfpReviewPage from "./pages/EvaluateRfpReviewPage";
import RequireWorkspaceAuth from "./components/RequireWorkspaceAuth";
import ImprovePage from "./pages/ImprovePage";
import ImproveIntakePage from "./pages/ImproveIntakePage";
import Module1Page from "./pages/Module1Page";
import Module2Page from "./pages/Module2Page";
import Module3Page from "./pages/Module3Page";
import SubmissionConfirmationPage from "./pages/SubmissionConfirmationPage";
import ResumeAssessmentPage from "./pages/ResumeAssessmentPage";
import { AdminLayout } from "./admin/AdminLayout";
import QuestionsPage from "./admin/QuestionsPage";
import TriggersPage from "./admin/TriggersPage";
import AssessmentsListPage from "./consultant/AssessmentsListPage";
import AssessmentDetailPage from "./consultant/AssessmentDetailPage";
import SmartImproveListPage from "./consultant/SmartImproveListPage";
import SmartImproveDetailPage from "./consultant/SmartImproveDetailPage";
import DemoRequestsPage from "./consultant/DemoRequestsPage";
import SmartEwmBotPage from "./consultant/SmartEwmBotPage";
import SmartEwmLayout from "./smartewm/SmartEwmLayout";
import { ConsultantLayout } from "./consultant/ConsultantLayout";
import CapabilityMatrixPage from "./admin/CapabilityMatrixPage";
import RecommendationsPage from "./admin/RecommendationsPage";
import RoiPage from "./admin/RoiPage";
import DemoProfilesPage from "./admin/DemoProfilesPage";
import DemoProfileValidationPage from "./admin/DemoProfileValidationPage";
import ValidationProfilesPage from "./admin/ValidationProfilesPage";
import ValidationProfileDetailPage from "./admin/ValidationProfileDetailPage";
import BrandingPage from "./admin/BrandingPage";
import UsersPage from "./admin/UsersPage";
import RecentPrivaRunsPage from "./admin/RecentPrivaRunsPage";
import SignalHealthPage from "./admin/SignalHealthPage";
import SapConnectionsPage from "./admin/SapConnectionsPage";
import SignalCatalogPage from "./admin/SignalCatalogPage";
import SmartEWMConfigPage from "./admin/SmartEWMConfigPage";
import ClientMasterPage from "./admin/ClientMasterPage";
import SapSystemMasterPage from "./admin/SapSystemMasterPage";
import ForgotPasswordPage from "./admin/ForgotPasswordPage";
import ResetPasswordPage from "./admin/ResetPasswordPage";
import LogicReferencePage from "./admin/LogicReferencePage";
// NB: ./admin/AiPromptsPage is intentionally NOT imported. The file is kept
// on disk and the backend endpoints remain active so data is preserved, but
// the route is hidden from the Admin UI.
import EmailTemplatesPage from "./admin/EmailTemplatesPage";
import EvaluateClientCommunicationPage from "./admin/EvaluateClientCommunicationPage";
import ImplementationTemplatesPage from "./admin/ImplementationTemplatesPage";
import ReferenceModelsPage from "./admin/ReferenceModelsPage";
import AiAnalysisPromptsPage from "./admin/AiAnalysisPromptsPage";
import GuidelinesPage from "./admin/GuidelinesPage";
import SmartBidPlaybookPage from "./admin/SmartBidPlaybookPage";
import SmartBidValidationProfilesPage from "./admin/SmartBidValidationProfilesPage";
import SmartInspectLandingPage from "./admin/SmartInspectLandingPage";
import SmartInspectQuestionsPage from "./admin/SmartInspectQuestionsPage";
import SmartInspectRoiFormulasPage from "./admin/SmartInspectRoiFormulasPage";
import SmartInspectCommercialPackagesPage from "./admin/SmartInspectCommercialPackagesPage";
import SmartInspectCountryCurrencyPage from "./admin/SmartInspectCountryCurrencyPage";
import SmartInspectRoiAssessmentsPage from "./admin/SmartInspectRoiAssessmentsPage";
import SmartInspectRoiDetailPage from "./admin/SmartInspectRoiDetailPage";
import SmartInspectClientCommunicationPage from "./admin/SmartInspectClientCommunicationPage";
import SmartInspectPlaybookPage from "./admin/SmartInspectPlaybookPage";
import SmartInspectInstructionsPage from "./admin/SmartInspectInstructionsPage";
import SmartEwmPlaybookPage from "./admin/SmartEwmPlaybookPage";
import HelpKnowledgePage from "./admin/HelpKnowledgePage";
import {
  KnowledgeLibraryPage as SiKnowledgeLibraryPage,
} from "./admin/smartimprove/SmartImprovePages";
import SmartImprovePlaybookPage from "./admin/SmartImprovePlaybookPage";
import SiAiGuidancePage from "./admin/smartimprove/NarrativeGuidancePage";
import SiRoiModelsPage from "./admin/smartimprove/RoiModelsPage";
import SiValueBenchmarksPage from "./admin/smartimprove/ValueBenchmarksPage";
import SiOperationalAreasPage from "./admin/smartimprove/BusinessProcessesPage";
import SiInsightTemplatesPage from "./admin/smartimprove/InsightTemplatesPage";
import SiRecommendationTemplatesPage from "./admin/smartimprove/RecommendationTemplatesPage";
import SiImplementationTemplatesPage from "./admin/smartimprove/ImplementationTemplatesPage";
import SiIssueLibraryPage from "./admin/smartimprove/IssueTemplatesPage";
import SiKpiLibraryPage from "./admin/smartimprove/KpiTemplatesPage";
import SiEngagementDefaultsPage from "./admin/smartimprove/EngagementDefaultsPage";
import SiDemoProfilesPage from "./admin/smartimprove/DemoProfilesPage";
import { ReportTemplatePage as SiReportTemplatePage } from "./admin/smartimprove/SmartImproveSingletonPages";
import SiClientCommunicationPage from "./admin/smartimprove/ClientCommunicationPage";
import PrintReport from "./print/PrintReport";
import PrintSmartInspectReport from "./print/PrintSmartInspectReport";
import PlatformHome from "./pages/PlatformHome";
import TransformationPortal from "./pages/TransformationPortal";
import TransformationDashboard from "./pages/TransformationDashboard";
import TransformationDemo from "./pages/TransformationDemo";
import ClientActivation from "./pages/ClientActivation";
import MyEvaluationsPage from "./pages/MyEvaluationsPage";
import ClientEvaluationView from "./consultant/ClientEvaluationView";
import {
  SmartExecutionPage,
  SmartAccelerationPage,
  SmartWarehouseTechPage,
  SmartIntegrationsPage,
} from "./pages/PillarPages";
import RequestDemoModal from "./components/RequestDemoModal";
import BackToTop from "./components/BackToTop";
import PrivacyConsent from "./components/PrivacyConsent";
import ScrollToTopOnRouteChange from "./components/ScrollToTopOnRouteChange";
import SmartInspectPage from "./pages/SmartInspectPage";
import SmartInspectRoiPage from "./pages/SmartInspectRoiPage";
import SmartInspectWorkspacePage from "./consultant/SmartInspectWorkspacePage";
import SmartInspectAssessmentPlaceholderPage from "./consultant/SmartInspectAssessmentPlaceholderPage";
import SmartInspectAssessmentDetailPage from "./consultant/SmartInspectAssessmentDetailPage";
import SmartRFPage from "./pages/SmartRFPage";
import PrimeS4Home from "./pages/PrimeS4Home";
import ComingSoonPage from "./pages/ComingSoonPage";
import PrivaPage from "./pages/PrivaPage";
import PlatformsPage from "./pages/PlatformsPage";
import InnovationPage from "./pages/InnovationPage";
import CoInnovatePage from "./pages/CoInnovatePage";
import EnterpriseAppsPage from "./pages/EnterpriseAppsPage";

/**
 * Wildcard fallback that rewrites any unmatched nested path under a new
 * standardized base (e.g. `/warehousex/smartstrategy/smartevaluate/*`) to the
 * corresponding legacy base (e.g. `/evaluate/*`).
 *
 * - More-specific Route entries (defined below) win over this wildcard, so
 *   known nested routes render the SAME component without redirecting (clean
 *   URL preserved).
 * - Any future nested route added under the legacy base becomes automatically
 *   reachable under the new base via this redirect — no code change needed
 *   per-route.
 * - Preserves the trailing path segment(s), query string, and hash so deep
 *   links / state hand-off keep working end-to-end.
 */
function RedirectToLegacyBase({ base }) {
  const params = useParams();
  const location = useLocation();
  const rest = params["*"] || "";
  const target = `${base}${rest ? `/${rest}` : ""}${location.search || ""}${location.hash || ""}`;
  return <Navigate to={target} replace />;
}

const Home = () => {
  const [userEmail, setUserEmail] = useState(null);

  const checkSession = useCallback(async () => {
    const storedEmail = localStorage.getItem("warehousex_email");
    if (storedEmail) {
      setUserEmail(storedEmail);
      try {
        await getSession(storedEmail);
      } catch (e) {
        // No active session found
      }
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">
        <HeroSection />
        <ModuleCards userEmail={userEmail} />
      </main>
      <Footer />
    </div>
  );
};

function RedirectAssignment({ module }) {
  const { id } = useParams();
  return <Navigate to={`/transformation/evaluations/${module}/${id}`} replace />;
}

function App() {
  return (
    <HelmetProvider>
      <div>
        <Toaster position="top-right" richColors closeButton />
        <RequestDemoModal />
        <PrivacyConsent />
        <BrowserRouter>
        <ScrollToTopOnRouteChange />
        <BackToTop />
        <Routes>
          {/* --- PrimeS4 AI ecosystem top-level routes (Phase 1) --------- */}
          {/* New root is the PrimeS4 AI ecosystem home. The previous root
              (WarehouseX landing) is now reachable at /warehousex which is
              kept as a first-class route. */}
          <Route path="/" element={<PrimeS4Home />} />
          <Route path="/priva" element={<PrivaPage />} />
          <Route path="/platforms" element={<PlatformsPage />} />
          <Route
            path="/enterprise-apps"
            element={<EnterpriseAppsPage />}
          />
          <Route
            path="/innovation"
            element={<InnovationPage />}
          />
          <Route
            path="/coinnovate"
            element={<CoInnovatePage />}
          />
          <Route
            path="/partners"
            element={
              <ComingSoonPage
                eyebrow="Partners"
                title="PrimeS4 AI Partners"
                description="System integrators, technology partners, and channel partners across the PrimeS4 AI ecosystem. Coming soon."
              />
            }
          />
          <Route
            path="/about"
            element={
              <ComingSoonPage
                eyebrow="About"
                title="About PrimeS4 AI"
                description="The AI, Innovation & Intelligent Platforms ecosystem of PrimeS4. Full story coming soon."
              />
            }
          />
          <Route
            path="/contact"
            element={
              <ComingSoonPage
                eyebrow="Contact"
                title="Contact PrimeS4 AI"
                description="Reach out to the PrimeS4 AI team. Detailed contact options coming soon."
              />
            }
          />

          {/* --- WarehouseX platform (new standardized hierarchy) -------- */}
          {/* The WarehouseX landing keeps its existing implementation; the
              new /warehousex/* routes simply mount the same components under
              the standardized hierarchy. Old top-level routes below remain
              wired for backward compatibility. */}
          <Route path="/warehousex" element={<PlatformHome />} />
          <Route path="/warehousex/smartstrategy" element={<Home />} />

          {/* SmartEvaluate — standardized nested hierarchy. Each known nested
              path mounts the SAME component as the legacy /evaluate/* route
              (no redirect → clean URL). The wildcard fallback at the bottom
              rewrites any unknown nested sub-path to the legacy base so any
              future /evaluate/<x> route works under the new base
              automatically. Order matters: specific routes are listed
              BEFORE the wildcard so React Router v6 ranks them higher. */}
          <Route path="/warehousex/smartstrategy/smartevaluate" element={<EvaluatePage />} />
          <Route path="/warehousex/smartstrategy/smartevaluate/select-mode" element={<Navigate to="/warehousex/smartstrategy/smartevaluate" replace />} />
          <Route element={<RequireWorkspaceAuth />}>
            <Route path="/warehousex/smartstrategy/smartevaluate/rfp-client-info" element={<EvaluateRfpClientInfoPage />} />
            <Route path="/warehousex/smartstrategy/smartevaluate/rfp-intake" element={<EvaluateRfpIntakePage />} />
            <Route path="/warehousex/smartstrategy/smartevaluate/rfp-review" element={<EvaluateRfpReviewPage />} />
          </Route>
          <Route path="/warehousex/smartstrategy/smartevaluate/resume" element={<ResumeAssessmentPage />} />
          <Route path="/warehousex/smartstrategy/smartevaluate/module-1" element={<Module1Page />} />
          <Route path="/warehousex/smartstrategy/smartevaluate/module-2" element={<Module2Page />} />
          <Route path="/warehousex/smartstrategy/smartevaluate/module-3" element={<Module3Page />} />
          <Route path="/warehousex/smartstrategy/smartevaluate/module-4" element={<SubmissionConfirmationPage />} />
          <Route path="/warehousex/smartstrategy/smartevaluate/submitted" element={<SubmissionConfirmationPage />} />
          <Route path="/warehousex/smartstrategy/smartevaluate/*" element={<RedirectToLegacyBase base="/evaluate" />} />

          {/* SmartImprove — same pattern as SmartEvaluate above. */}
          <Route path="/warehousex/smartstrategy/smartimprove" element={<ImprovePage />} />
          <Route path="/warehousex/smartstrategy/smartimprove/start" element={<ImproveIntakePage />} />
          <Route path="/warehousex/smartstrategy/smartimprove/*" element={<RedirectToLegacyBase base="/improve" />} />

          <Route path="/warehousex/smartexecution" element={<SmartExecutionPage />} />
          <Route path="/warehousex/smartexecution/smartrf" element={<SmartRFPage />} />
          <Route path="/warehousex/smartacceleration" element={<SmartAccelerationPage />} />
          <Route path="/warehousex/smartwarehouse-tech" element={<SmartWarehouseTechPage />} />
          <Route path="/warehousex/smartintegrations" element={<SmartIntegrationsPage />} />
          <Route path="/warehousex/smartintegrations/smartinspect" element={<SmartInspectPage />} />
          <Route path="/warehousex/smartintegrations/smartinspect/roi" element={<SmartInspectRoiPage />} />
          <Route path="/smartintegrations/smartinspect/roi" element={<SmartInspectRoiPage />} />

          {/* --- Legacy routes (kept active for backward compatibility) -- */}
          <Route path="/smartstrategy" element={<Home />} />
          <Route path="/smartexecution" element={<SmartExecutionPage />} />
          <Route path="/smartexecution/smartrf" element={<SmartRFPage />} />
          <Route path="/smartacceleration" element={<SmartAccelerationPage />} />
          <Route path="/smartwarehouse-tech" element={<SmartWarehouseTechPage />} />
          <Route path="/smartintegrations" element={<SmartIntegrationsPage />} />
          <Route path="/smartintegrations/smartinspect" element={<SmartInspectPage />} />
          <Route path="/smartsuite" element={<Navigate to="/warehousex/smartstrategy" replace />} />
          <Route path="/evaluate" element={<EvaluatePage />} />
          <Route path="/evaluate/select-mode" element={<Navigate to="/warehousex/smartstrategy/smartevaluate" replace />} />
          <Route element={<RequireWorkspaceAuth />}>
            <Route path="/evaluate/rfp-client-info" element={<EvaluateRfpClientInfoPage />} />
            <Route path="/evaluate/rfp-intake" element={<EvaluateRfpIntakePage />} />
            <Route path="/evaluate/rfp-review" element={<EvaluateRfpReviewPage />} />
          </Route>
          <Route path="/smartevaluate" element={<Navigate to="/evaluate" replace />} />
          <Route path="/improve" element={<ImprovePage />} />
          <Route path="/improve/start" element={<ImproveIntakePage />} />
          <Route path="/smartimprove" element={<Navigate to="/improve" replace />} />
          <Route path="/evaluate/resume" element={<ResumeAssessmentPage />} />
          <Route path="/evaluate/module-1" element={<Module1Page />} />
          <Route path="/evaluate/module-2" element={<Module2Page />} />
          <Route path="/evaluate/module-3" element={<Module3Page />} />
          <Route path="/evaluate/module-4" element={<SubmissionConfirmationPage />} />
          <Route path="/evaluate/submitted" element={<SubmissionConfirmationPage />} />
          <Route path="/transformation" element={<TransformationPortal />} />
          <Route path="/transformation/activate/:token" element={<ClientActivation />} />
          <Route path="/transformation/dashboard" element={<TransformationDashboard />} />
          {/* Client Access Management — client-facing "My Evaluations" +
              read-only evaluation views. Rendered inside the shared
              application shell (ConsultantLayout) so clients get the same
              layout/sidebar as staff (restricted nav). Independent of the
              legacy Client Portal (/transformation/dashboard), left untouched. */}
          <Route path="/transformation/evaluations" element={<ConsultantLayout />}>
            <Route index element={<MyEvaluationsPage />} />
            <Route path="smartevaluate/:id" element={<ClientEvaluationView module="smartevaluate" />} />
            <Route path="smartimprove/:id" element={<ClientEvaluationView module="smartimprove" />} />
          </Route>
          {/* Backward-compat redirects for the previous "assignments" URLs. */}
          <Route path="/transformation/assignments" element={<Navigate to="/transformation/evaluations" replace />} />
          <Route path="/transformation/assignments/smartevaluate/:id" element={<RedirectAssignment module="smartevaluate" />} />
          <Route path="/transformation/assignments/smartimprove/:id" element={<RedirectAssignment module="smartimprove" />} />
          <Route path="/transformation/demo" element={<TransformationDemo />} />
          <Route path="/admin/login" element={<Navigate to="/transformation" replace />} />
          <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/admin/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="questions" element={<QuestionsPage />} />
            <Route path="triggers" element={<TriggersPage />} />
            <Route path="capability-matrix" element={<CapabilityMatrixPage />} />
            <Route path="recommendations" element={<RecommendationsPage />} />
            <Route path="roi-assumptions" element={<RoiPage />} />
            <Route path="demo-profiles" element={<DemoProfilesPage />} />
            <Route path="demo-profiles/validation" element={<DemoProfileValidationPage />} />
            <Route path="validation-profiles" element={<ValidationProfilesPage />} />
            <Route path="validation-profiles/:id" element={<ValidationProfileDetailPage />} />
            <Route path="demo-data" element={<DemoProfilesPage />} />
            <Route path="branding" element={<BrandingPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="logic-reference" element={<LogicReferencePage />} />
            {/* AI Prompts hidden from the UI — keep the backend and the
                saved prompts intact for future use. Any stale deep-link or
                bookmark lands on the Logic Reference blueprint. */}
            <Route path="ai-prompts" element={<Navigate to="/admin/logic-reference" replace />} />
            <Route path="email-templates" element={<EmailTemplatesPage />} />
            <Route path="evaluate/client-communication" element={<EvaluateClientCommunicationPage />} />
            <Route path="implementation-templates" element={<ImplementationTemplatesPage />} />
            <Route path="reference-models" element={<ReferenceModelsPage />} />
            <Route path="ai-analysis-prompts" element={<AiAnalysisPromptsPage />} />
            {/* Per-application AI Analysis Prompts — single shared backend
                repository, filtered by owning application. */}
            <Route path="smartevaluate/ai-analysis-prompts" element={<AiAnalysisPromptsPage application="SmartEvaluate" />} />
            <Route path="smartbid/ai-analysis-prompts" element={<AiAnalysisPromptsPage application="SmartBid" />} />
            <Route path="smartimprove/ai-analysis-prompts" element={<AiAnalysisPromptsPage application="SmartImprove" />} />
            <Route path="smartewm/ai-analysis-prompts" element={<AiAnalysisPromptsPage application="SmartEWM" />} />
            <Route path="smartbid/playbook" element={<SmartBidPlaybookPage />} />
            <Route path="smartbid/validation-profiles" element={<SmartBidValidationProfilesPage />} />
            <Route path="smartinspect" element={<SmartInspectLandingPage />} />
            <Route path="smartinspect/questions" element={<SmartInspectQuestionsPage />} />
            <Route path="smartinspect/roi-formulas" element={<SmartInspectRoiFormulasPage />} />
            <Route path="smartinspect/country-currency" element={<SmartInspectCountryCurrencyPage />} />
            <Route path="smartinspect/commercial-packages" element={<SmartInspectCommercialPackagesPage />} />
            <Route path="smartinspect/roi-assessments" element={<SmartInspectRoiAssessmentsPage />} />
            <Route path="smartinspect/roi-assessments/:id" element={<SmartInspectRoiDetailPage />} />
            <Route path="smartinspect/client-communication" element={<SmartInspectClientCommunicationPage />} />
            <Route path="smartinspect/instructions" element={<SmartInspectInstructionsPage />} />
            <Route path="smartinspect/playbook" element={<SmartInspectPlaybookPage />} />
            <Route path="smartewm/playbook" element={<SmartEwmPlaybookPage />} />
            <Route path="guidelines" element={<GuidelinesPage />} />
            <Route path="help-knowledge" element={<HelpKnowledgePage />} />
            {/* SmartImprove admin/configuration — Phase 2. All routes mount
                under /admin/smartimprove/* so SmartEvaluate routes are
                completely untouched. */}
            <Route path="smartimprove/operational-areas" element={<SiOperationalAreasPage />} />
            <Route path="smartimprove/issue-library" element={<SiIssueLibraryPage />} />
            <Route path="smartimprove/kpi-library" element={<SiKpiLibraryPage />} />
            <Route path="smartimprove/insight-templates" element={<SiInsightTemplatesPage />} />
            <Route path="smartimprove/recommendation-templates" element={<SiRecommendationTemplatesPage />} />
            <Route path="smartimprove/roi-models" element={<SiRoiModelsPage />} />
            <Route path="smartimprove/value-benchmarks" element={<SiValueBenchmarksPage />} />
            <Route path="smartimprove/implementation-templates" element={<SiImplementationTemplatesPage />} />
            <Route path="smartimprove/report-template" element={<SiReportTemplatePage />} />
            <Route path="smartimprove/client-communication" element={<SiClientCommunicationPage />} />
            <Route path="smartimprove/playbook" element={<SmartImprovePlaybookPage />} />
            <Route path="smartimprove/knowledge-library" element={<SiKnowledgeLibraryPage />} />
            <Route path="smartimprove/ai-guidance" element={<SiAiGuidancePage />} />
            <Route path="smartimprove/engagement-defaults" element={<SiEngagementDefaultsPage />} />
            <Route path="smartimprove/demo-profiles" element={<SiDemoProfilesPage />} />
          </Route>
          <Route path="/consultant" element={<ConsultantLayout />}>
            <Route path="assessments" element={<AssessmentsListPage />} />
            <Route path="assessments/:id" element={<AssessmentDetailPage />} />
            <Route path="smartbid" element={<AssessmentsListPage variant="rfp" />} />
            <Route path="smartbid/:id" element={<AssessmentDetailPage />} />
            <Route path="smartimprove" element={<SmartImproveListPage />} />
            <Route path="smartimprove/:id" element={<SmartImproveDetailPage />} />
            <Route path="smartinspect" element={<SmartInspectWorkspacePage />} />
            <Route path="smartinspect/new" element={<SmartInspectAssessmentPlaceholderPage />} />
            <Route path="smartinspect/:id" element={<SmartInspectAssessmentDetailPage />} />
            <Route path="demo-requests" element={<DemoRequestsPage />} />
          </Route>
          {/* SmartEWM application shell — fully separate from the consultant
              workspace. Only users with application_access="SmartEWM" can
              enter this tree (enforced inside SmartEwmLayout). */}
          <Route path="/app/smartewmbot" element={<SmartEwmLayout />}>
            <Route index element={<SmartEwmBotPage />} />
          </Route>
          {/* PRIVA Governance — runtime operational AI monitoring lives in
              the consultant/workspace shell (NOT the admin configuration
              shell). The URL stays `/admin/priva/*` for back-compat and
              because admins still own the surface; the route is admin-
              only via the backend role gate and the sidebar group is
              conditionally rendered for admin users only. */}
          <Route path="/admin/priva/runs" element={<ConsultantLayout />}>
            <Route index element={<RecentPrivaRunsPage />} />
          </Route>
          <Route path="/admin/priva/signal-health" element={<ConsultantLayout />}>
            <Route index element={<SignalHealthPage />} />
          </Route>
          <Route path="/admin/priva/clients" element={<ConsultantLayout />}>
            <Route index element={<ClientMasterPage />} />
          </Route>
          <Route path="/admin/priva/sap-systems" element={<ConsultantLayout />}>
            <Route index element={<SapSystemMasterPage />} />
          </Route>
          <Route path="/admin/priva/sap-connections" element={<ConsultantLayout />}>
            <Route index element={<SapConnectionsPage />} />
          </Route>
          <Route path="/admin/priva/signal-catalog" element={<ConsultantLayout />}>
            <Route index element={<SignalCatalogPage />} />
          </Route>
          <Route path="/admin/priva/smartewm-config" element={<ConsultantLayout />}>
            <Route index element={<SmartEWMConfigPage />} />
          </Route>
          {/* Canonical Consultant Dashboard route — same component tree as
              /consultant so every existing deep-link + back-link keeps working. */}
          <Route path="/app" element={<ConsultantLayout />}>
            <Route path="assessments" element={<AssessmentsListPage />} />
            <Route path="assessments/:id" element={<AssessmentDetailPage />} />
            <Route path="smartbid" element={<AssessmentsListPage variant="rfp" />} />
            <Route path="smartbid/:id" element={<AssessmentDetailPage />} />
            <Route path="smartimprove" element={<SmartImproveListPage />} />
            <Route path="smartimprove/:id" element={<SmartImproveDetailPage />} />
            <Route path="smartinspect" element={<SmartInspectWorkspacePage />} />
            <Route path="smartinspect/new" element={<SmartInspectAssessmentPlaceholderPage />} />
            <Route path="smartinspect/:id" element={<SmartInspectAssessmentDetailPage />} />
            <Route path="demo-requests" element={<DemoRequestsPage />} />
          </Route>
          <Route path="/print/report/:token" element={<PrintReport />} />
          <Route path="/print/smartinspect/:token" element={<PrintSmartInspectReport />} />
        </Routes>
      </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
